/* eslint-disable prettier/prettier */
import jsPDF from "jspdf";
import { MonsterEntity, attackDiceMap, DEFAULT_REACTION, PowerLevel } from "../../../constants/Monster";
import { AuthState } from "../../../context/AuthState";
import {
  addEvenlySpacedText,
  addGroupedText,
  addImage,
  addText,
  initializePdf,
  labelValues,
  TextAndSettingPair,
  TEXT_SETTINGS,
} from "./InitializePdf";

export async function uploadMonsterPdf(
  monsterProps: MonsterEntity,
  authState: AuthState,
  s3Key: string,
): Promise<void> {
  const doc = initializePdf();
  let height = 50;
  height = addText(monsterProps.name, TEXT_SETTINGS.Title, doc, height);
  height = addText(`Power Level: ${monsterProps.powerLevel}`, TEXT_SETTINGS.Subtitle, doc, height);
  height += 10;
  height = addText(
    `Motivation: ${monsterProps.motivation}`,
    TEXT_SETTINGS.Subtitle,
    doc,
    height
  );
  height += 5;
  let strengthAndWeaknessText = "";
  if (monsterProps.strengths.length !== 0) {
    strengthAndWeaknessText += "Resistant: " + monsterProps.strengths.join(", ") + " | ";
  }
  if (monsterProps.weaknesses.length !== 0) {
    strengthAndWeaknessText += "Vulnerable: " + monsterProps.weaknesses.join(", ");
  }
  if (strengthAndWeaknessText !== "") {
    height = addText(strengthAndWeaknessText, TEXT_SETTINGS.Standard, doc, height, 40);
    height += 10;
  }
  if (monsterProps.previewS3Key !== "") {
    const presignedUrl = await authState.getPresignedUrlWithKey(monsterProps.previewS3Key);

    await addImage(presignedUrl, doc, height);
    height += 320;
  }

  const reactionLabel = monsterProps.powerLevel === PowerLevel.Boss || monsterProps.powerLevel === PowerLevel.MegaBoss ? "Mega-Reactions" : "Reactions";
  const statLabels = ["Health", "Armor", "Speed", "Actions", reactionLabel];
  const statValues = [
    monsterProps.health,
    monsterProps.armor,
    monsterProps.speed,
    monsterProps.actions,
    monsterProps.reactions
  ];
  const statTexts = labelValues(statLabels, statValues);
  height = addEvenlySpacedText(statTexts, TEXT_SETTINGS.Stat, doc, height);
  height += 10;

  const abilityLabels = ["STR", "AGI", "INT", "WIS", "CHA"];
  const abilityValues = [
    monsterProps.str,
    monsterProps.dex,
    monsterProps.int,
    monsterProps.wis,
    monsterProps.cha,
  ];
  const abilityTexts = labelValues(abilityLabels, abilityValues);
  height = addEvenlySpacedText(abilityTexts, TEXT_SETTINGS.Standard, doc, height);
  height += 20;

  let attackText = "";
  if (monsterProps.attackTarget.includes("Spell")) {
    attackText += `${monsterProps.attackTarget}`;
  } else {
    attackText += "Attack";
  }
  if (monsterProps.damageType !== "Weapon") {
    attackText += ` (${monsterProps.damageType})`
  }
  attackText += ":"
  const attackDiceText = attackDiceMap.get(monsterProps.attackDice)?.replaceAll("+", " + ");
  attackText += ` ${attackDiceText} + ${monsterProps.attackBonus}, ${monsterProps.attackRange}ft range`;
  if (monsterProps.attackRadius > 0) {
    attackText += `, ${monsterProps.attackRadius}ft radius!`;
  } else {
    attackText += "."
  }
  height = addText(attackText, TEXT_SETTINGS.Rocker, doc, height);

  // Attack Modifiers
  height = documentAttackDetails(doc, monsterProps, height);

   const descriptionTexts = [
    { text: "Description", textSetting: TEXT_SETTINGS.Subtitle },
    { text: monsterProps.description, textSetting: TEXT_SETTINGS.Standard },
  ];
  const backgroundTexts = [
    { text: "Background", textSetting: TEXT_SETTINGS.Subtitle },
    { text: monsterProps.background, textSetting: TEXT_SETTINGS.Standard },
  ];

  if (monsterProps.description !== "") {
    height = addGroupedText(descriptionTexts, doc, height);
    height += 50;
  }
  if (monsterProps.background !== "") {
    height = addGroupedText(backgroundTexts, doc, height);
  }
  await authState.uploadFile(s3Key, doc.output("blob"));
}

export function documentAttackDetails(doc: jsPDF, monsterProps: MonsterEntity, height: number): number {
  if (monsterProps.attackModifier1 !== "") {
    height = addText(computeAttackModifierDescription(monsterProps.attackModifier1, monsterProps.attackModifierUses1), TEXT_SETTINGS.Standard, doc, height);
  }
  if (monsterProps.attackModifier2 !== "") {
    height = addText(computeAttackModifierDescription(monsterProps.attackModifier2, monsterProps.attackModifierUses2), TEXT_SETTINGS.Standard, doc, height);
  }
  if (monsterProps.attackModifier3 !== "") {
    height = addText(computeAttackModifierDescription(monsterProps.attackModifier3, monsterProps.attackModifierUses3), TEXT_SETTINGS.Standard, doc, height);
  }

  if (monsterProps.reactions > 0) {
    height += 20;
    for (let reactionNumber = 1; reactionNumber <= 5; reactionNumber++) {
      const descriptionKey = "reactionDescription" + reactionNumber;
      const usesKey = "reactionUses" + reactionNumber;
      const reactionDescription = monsterProps[descriptionKey];
      const reactionUses = monsterProps[usesKey];
      if (reactionNumber < 1) {
        break;
      } else if (reactionDescription === "") {
        continue;
      } else {
        let reactionType = 'Reaction'
        if (monsterProps.powerLevel === PowerLevel.Boss || monsterProps.powerLevel === PowerLevel.MegaBoss) {
          reactionType = 'Mega-Reaction'
        }
        const reactionText = `${reactionType} ${reactionUses} Uses: ${reactionDescription}.`;
        height = addText(reactionText, TEXT_SETTINGS.Standard, doc, height);
      }
    }
    height += 20;
  }

  height += 50;

  const strategyTexts: TextAndSettingPair[] = [
    { text: "Special Notes", textSetting: TEXT_SETTINGS.Subtitle },
    { text: monsterProps.specialNotes, textSetting: TEXT_SETTINGS.Standard },
  ];
  if (monsterProps.specialNotes !== "") {
    height = addGroupedText(strategyTexts, doc, height);
    height += 50;
  }

  return height;
}

function computeAttackModifierDescription(attackModifier: string, uses: number) {
    return `Modify Attack (${uses} uses): ${attackModifier}.`;
}